import { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Box, Typography, Card, Stack, Button, Divider,
} from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import { FormProvider, RHFSelect, RHFTextField } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import CRUD from 'utils/Crud';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 480,
  maxWidth: '98vw',
  bgcolor: 'background.paper',
  border: '2px solid whitesmoke',
  boxShadow: 24,
  borderRadius: 3,
  p: 2,
};

const REJECTION_REASONS = [
  { value: '', label: '-- Select Reason --', prefill: '' },
  { value: 'bad_card', label: 'Bad card', prefill: 'The card you submitted was rejected because it is invalid or unreadable.' },
  { value: 'used_card', label: 'Used card', prefill: 'The card you submitted has already been used and cannot be accepted.' },
  { value: 'incorrect_details', label: 'Incorrect details', prefill: 'The details provided do not match the card. Please check and resubmit.' },
  { value: 'additional_info', label: 'Requires additional information', prefill: 'We need more information to process your card. Please contact support.' },
  { value: 'no_buyer', label: 'No Active Buyer', prefill: 'Currently, there is no active buyer for this card type. Please try again later.' },
  {
    value: 'duplicate',
    label: 'Duplicate Order',
    prefill: 'This card has already been submitted in a previous order. Duplicate submissions are not allowed.'
  },
  {
    value: 'zombie_card',
    label: 'Zombie Card',
    prefill: 'This voucher is no longer valid so it cannot be exchanged.'
  }
  
];

const NewUserSchema = Yup.object().shape({
  reason: Yup.string().required('Please select a reason'),
  remark: Yup.string().required('Remark is required'),
  password: Yup.string().required('Password is required'),
});

RejectGiftcardModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleComplete: PropTypes.func,
  order: PropTypes.object.isRequired
};

export default function RejectGiftcardModal({ open, handleClose, handleComplete, order }) {
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: { reason: '', password: '', remark: '' },
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const selectedReason = watch('reason');

  useEffect(() => {
    const found = REJECTION_REASONS.find(r => r.value === selectedReason);
    if (found && found.prefill) {
      setValue('remark', found.prefill, { shouldValidate: true });
    }
  }, [selectedReason, setValue]);

  const onSubmit = async (values) => {
    try {
      values.order_id = order.order_id;
      const ret = await axiosInstance.post(CRUD.order.giftcardOrderReject, values);
      enqueueSnackbar('Action completed successfully!');
      reset();
      handleComplete(ret.data);
    //   console.log(ret.data)
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error));
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      hideBackdrop={false}
    >
      <Box sx={style}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            <Typography id="modal-modal-title" variant="h6" fontWeight={700} mb={1}>
              Reject Order
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <div>
                <Typography variant="subtitle2" fontWeight={600}>
                  Rejection Reason
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Select a reason for rejecting this gift card order.
                </Typography>
                <RHFSelect name="reason" sx={{ mt: 1, width: '100%' }}>
                  {REJECTION_REASONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </RHFSelect>
              </div>

              <div>
                <Typography variant="subtitle2" fontWeight={600}>
                  Remark
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This message will be sent to the user as the reason for rejection. You can edit it if needed.
                </Typography>
                <RHFTextField
                  sx={{ mt: 1 }}
                  name="remark"
                  required
                  multiline
                  minRows={2}
                  maxRows={3}
                  placeholder="Type or select a reason above to prefill"
                />
              </div>

              <div>
                <Typography variant="subtitle2" fontWeight={600}>
                  Confirm Password
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Enter your password to confirm this action.
                </Typography>
                <RHFTextField
                  name="password"
                  label="Password"
                  type="password"
                  sx={{ mt: 1 }}
                  required
                />
              </div>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  reset();
                  handleClose();
                }}
                size="large"
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                loading={isSubmitting}
              >
                Submit Review
              </LoadingButton>
            </Stack>
          </Card>
        </FormProvider>
      </Box>
    </Modal>
  );
}
